import { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import styled from "styled-components";
import { BookingData, IFareCart, IUpdateItem } from "@interfaces";
import { AddTravellerComponent } from "src/Components/AddTravellerComponent";
import { breakpoint } from "@utilities";
import { CartItemHeader } from "src/Components/CartItemHeader";

export interface Suggestion {
    first_name: string | null;
    last_name: string | null;
    phone_number: number;
    country_code: string;
    email: string;
    id: string;
}

interface NewCartItemProps {
    groupId: string;
    items: IFareCart[];
    quantities: Record<string | number, number>;
    onTravellerDataChange: (data: IUpdateItem[]) => void;
}

export const CheckoutItemModule = ({
    items,
    quantities,
    onTravellerDataChange,
}: NewCartItemProps) => {
    const firstItem = items[0];
    const prevTransformedDataRef = useRef<IUpdateItem[]>();

    const ProductData = _.find(
        firstItem.product?.json?.faresprices,
        (product) =>
            String(product.productPricesDetailsId) === String(firstItem.product_price_details_id),
    );

    const [confirmedTravellers, setConfirmedTravellers] = useState<{ [date: string]: Set<string> }>(
        {},
    );
    const [travellerDataByItem, setTravellerDataByItem] = useState<{ [itemId: string]: any[] }>({});
    const [pickupLocationsByItem, setPickupLocationsByItem] = useState<{
        [itemId: string]: {
            [index: number]: {
                pickupId: string | number;
                pickupLocation: string;
                pickupTime: string;
            };
        };
    }>({});
    const [bookingTimeByItem, setBookingTimeByItem] = useState<{
        [itemId: string]: {
            [index: number]: { bookingTimeId: string | number; commences: string };
        };
    }>({});

    const transformedData = useMemo(() => {
        return items.map((item) => {
            const itemQuantity = quantities[item.id] || item.booking_quantity || 1;
            const travellerArray = travellerDataByItem[item.id] || [];
            const pickupLocationMapping = pickupLocationsByItem[item.id] || {};
            const BookingTimeMapping = bookingTimeByItem[item.id] || {};

            const bookingData: BookingData[] = _.range(1, itemQuantity + 1).map((index) => ({
                quantityIndex: index,
                timeId: String(BookingTimeMapping[index - 1]?.bookingTimeId) || "",
                redeemers: travellerArray[index - 1]?.id ? [travellerArray[index - 1].id] : [],
                pickupLocation: pickupLocationMapping[index - 1]?.pickupLocation || "",
                pickupId:
                    pickupLocationMapping[index - 1]?.pickupId !== undefined
                        ? String(pickupLocationMapping[index - 1].pickupId)
                        : "",
                pickupTime: pickupLocationMapping[index - 1]?.pickupTime || "",
                commences: BookingTimeMapping[index - 1]?.commences || "",
            }));

            return {
                cartItemId: item.id,
                quantity: itemQuantity,
                bookingData,
            };
        });
    }, [items, quantities, travellerDataByItem, pickupLocationsByItem]);

    useEffect(() => {
        if (!_.isEqual(prevTransformedDataRef.current, transformedData)) {
            prevTransformedDataRef.current = transformedData;
            onTravellerDataChange(transformedData);
        }
    }, [transformedData, onTravellerDataChange]);

    const handlePickupLocationChangeForItem = (
        itemId: string | number,
        index: number,
        value: { pickupId: string | number; pickupLocation: string; pickupTime: string },
    ) => {
        setPickupLocationsByItem((prev) => ({
            ...prev,
            [itemId]: {
                ...(prev[itemId] || {}),
                [index]: value,
            },
        }));
    };

    const handleTimeChangeForItem = (
        itemId: string | number,
        index: number,
        value: { commences: string; bookingTimeId: string | number },
    ) => {
        setBookingTimeByItem((prev) => ({
            ...prev,
            [itemId]: {
                ...(prev[itemId] || {}),
                [index]: value,
            },
        }));
    };

    return (
        <Info>
            <div className="info">
                <CartItemHeader
                    item={firstItem}
                    />
                <Border />
                <div>{ProductData && <h2 className="fareName">{ProductData.fareName}</h2>}</div>
                {items.map((item) => (
                    <AddTravellerComponent
                        key={item.id}
                        item={item}
                        quantity={quantities[item.id]}
                        confirmedTravellers={confirmedTravellers}
                        setConfirmedTravellers={setConfirmedTravellers}
                        onTravellerDataChange={(data) =>
                            setTravellerDataByItem((prev) => ({
                                ...prev,
                                [item.id]: _.values(data),
                            }))
                        }
                        onPickupLocationChange={(index, value) =>
                            handlePickupLocationChangeForItem(item.id, index, value)
                        }
                        onTimeChange={(index, value) =>
                            handleTimeChangeForItem(item.id, index, value)
                        }
                    />
                ))}
            </div>
        </Info>
    );
};

const Info = styled.div`
    background-color: ${(props) => props.theme.colors.gray_6};
    padding-bottom: 0 !important;
    margin-bottom: 16px;

    .info {
        display: flex;
        flex-direction: column;
        background-color: #fff;
        padding: 16px;
        border-radius: 8px;

        .main {
            display: flex;
            gap: 12px;
        }

        .img {
            position: relative;
            cursor: pointer;
            height: 64px;
            width: 64px;
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                height: 100%;
                width: 100%;
                object-fit: cover; /* Ensures image fills the container */
                border-radius: 8px;
            }
        }

        .price-container {
            border: 1px solid #c3c9d0;
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            padding: 8px 20px;
            align-items: center;

            .price {
                display: flex;
                flex-direction: column;
                color: #6e7375;
                font-size: 12px;

                span {
                    color: #000;
                    font-size: 18px;
                }
            }

            .quantity {
                color: #6e7375;
                font-size: 12px;
                display: flex;
                align-items: center;
                gap: 10px;

                span {
                    font-size: 20px;
                    color: #1f2937;
                    width: 18px;
                    text-align: center;
                }
            }
        }
            .fareName{
                font-size: 18px;
                font-weight: 600;
            }
    }

    .name-container {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .title {
            font-size: 14px;
            font-weight: 600;
            line-height: 18px;
            cursor: pointer;
            color: #131214;
            text-decoration: underline;
            text-underline-offset: 1px;
            @media (min-width: 375px) {
                width: 240px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 290px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            ${breakpoint.breakLarge`
                width: 100%;
                font-size: 18px;
                font-weight: 700;
                line-height: 23px;
            `}
        }

        .sub-title {
            color: #6e7375;
            font-size: 12px;
            line-height: 16px;
            @media (min-width: 375px) {
                width: 240px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 290px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            ${breakpoint.breakLarge`
                width: 100%;      
            `}
        }
        .location {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #3c3c3c;
            font-size: 12px;
            font-weight: 500;
        }
    }
`;

const Border = styled.div`
    border-top: 1px solid #e6e9eb;
    margin: 10px 0px 14px 0px;
`;
